import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

const DEFAULT_COLLAPSE_TIMEOUT = 5_000; // 5 seconds
const CONFIG_URL =
  "https://storage.googleapis.com/cip-configs/sider-config.json";
const CACHE_KEY = "sider_auto_collapse_config";
const CACHE_TTL = 20 * 60 * 60 * 1_000; // 20 hours

interface CachedConfig {
  sider_hide_time: number;
  timestamp: number;
}

function getCachedConfig(): number | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedConfig = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL) return null;
    if (
      typeof cached.sider_hide_time !== "number" ||
      cached.sider_hide_time <= 0
    )
      return null;
    return cached.sider_hide_time;
  } catch {
    return null;
  }
}

function setCachedConfig(siderHideTime: number): void {
  try {
    const entry: CachedConfig = {
      sider_hide_time: siderHideTime,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage may be unavailable
  }
}

export const useSiderAutoCollapse = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [menuHover, setMenuHover] = useState(false);
  const defaultTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const configTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController>();

  const handleMenuHover = useCallback((value: boolean) => {
    setMenuHover(value);
  }, []);

  const handleCollapse = useCallback((value: boolean) => {
    setCollapsed(value);
  }, []);

  const onSiderItemClick = useCallback(() => {
    setCollapsed(false);
  }, []);

  useEffect(() => {
    if (collapsed) {
      return;
    }

    const startTime = Date.now();
    let cancelled = false;

    const collapse = () => {
      if (!cancelled) setCollapsed(true);
    };

    const applyConfigTime = (timeSeconds: number) => {
      clearTimeout(defaultTimerRef.current);
      const elapsed = Date.now() - startTime;
      const remaining = timeSeconds * 1_000 - elapsed;
      if (remaining <= 0) {
        collapse();
      } else {
        configTimerRef.current = setTimeout(collapse, remaining);
      }
    };

    defaultTimerRef.current = setTimeout(collapse, DEFAULT_COLLAPSE_TIMEOUT);

    if (CONFIG_URL) {
      const cached = getCachedConfig();
      if (cached !== null) {
        applyConfigTime(cached);
      } else {
        const controller = new AbortController();
        abortRef.current = controller;
        const separator = CONFIG_URL.includes("?") ? "&" : "?";
        axios
          .get(
            `${CONFIG_URL}${separator}cb=${Math.random()
              .toString(36)
              .slice(2)}`,
            { signal: controller.signal }
          )
          .then(({ data }) => {
            if (cancelled) return;
            const time = data?.sider_hide_time;
            if (typeof time === "number" && time > 0) {
              setCachedConfig(time);
              applyConfigTime(time);
            }
          })
          .catch(() => {
            // Request failed or invalid — default timer continues
          });
      }
    }

    return () => {
      cancelled = true;
      clearTimeout(defaultTimerRef.current);
      clearTimeout(configTimerRef.current);
      abortRef.current?.abort();
    };
  }, [collapsed]);

  return {
    menuHover,
    collapsed,
    handleCollapse,
    handleMenuHover,
    onSiderItemClick,
  };
};
